import React, { useState } from 'react';
import {
  User, Phone, Mail, MapPin, FileText, GraduationCap,
  Briefcase, CreditCard, AlertCircle, CheckCircle2,
  ChevronRight, ChevronLeft, Upload, Building2, Calendar,
  Heart, Shield, Loader2
} from 'lucide-react';

// ─── Replace with your actual Formspree form endpoint ───────────────────────
const FORM_ENDPOINT = 'https://formspree.io/f/mkokjnzr';
// ────────────────────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Personal Info',    icon: User },
  { id: 2, label: 'Identity Docs',   icon: Shield },
  { id: 3, label: 'Education',       icon: GraduationCap },
  { id: 4, label: 'Work History',    icon: Briefcase },
  { id: 5, label: 'Review & Submit', icon: CheckCircle2 },
];

const REQUIRED = ' *';

const initialForm = {
  // Step 1 – Personal
  fullName: '', dob: '', gender: '', bloodGroup: '',
  mobile: '', alternateMobile: '', personalEmail: '', officialEmail: '',
  currentAddress: '', permanentAddress: '', sameAddress: false,
  emergencyName: '', emergencyRelation: '', emergencyMobile: '',

  // Step 2 – Identity
  aadhaar: '', pan: '', passport: '',
  photoLink: '',

  // Step 3 – Education
  tenthSchool: '', tenthBoard: '', tenthYear: '', tenthPercent: '', tenthDocLink: '',
  twelfthSchool: '', twelfthBoard: '', twelfthYear: '', twelfthPercent: '',
  twelfthStream: '', twelfthDocLink: '',
  gradCollege: '', gradUniversity: '', gradDegree: '', gradBranch: '',
  gradYear: '', gradCgpa: '', gradDocLink: '',
  pgCollege: '', pgUniversity: '', pgDegree: '', pgBranch: '',
  pgYear: '', pgCgpa: '', pgDocLink: '',
  certifications: '',

  // Step 4 – Work
  joiningRole: '', joiningDept: '', joiningDate: '', employmentType: '',
  linkedin: '', github: '',
  prevCompany: '', prevDesignation: '', prevFrom: '', prevTo: '', prevReason: '',

  // Additional
  tshirtSize: '', dietary: '', medicalInfo: '',
  declaration: false,
};

const InputField = ({ label, required, error, children, hint }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    {error && <p className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{error}</p>}
  </div>
);

const cls = (base, error) =>
  `${base} ${error ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-blue-500'} focus:outline-none focus:ring-2 focus:border-transparent transition`;

const textInput = (error = false) =>
  cls('w-full px-4 py-2.5 rounded-xl border bg-white text-sm', error);

const selectInput = (error = false) =>
  cls('w-full px-4 py-2.5 rounded-xl border bg-white text-sm appearance-none', error);

export default function OnboardingPage() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  // ─── Validation per step ───────────────────────────────────────────────────
  const validate = (s) => {
    const e = {};
    if (s === 1) {
      if (!form.fullName.trim())        e.fullName        = 'Full name is required';
      if (!form.dob)                    e.dob             = 'Date of birth is required';
      if (!form.gender)                 e.gender          = 'Gender is required';
      if (!form.bloodGroup)             e.bloodGroup      = 'Blood group is required';
      if (!/^\d{10}$/.test(form.mobile)) e.mobile         = 'Enter a valid 10-digit mobile number';
      if (!form.personalEmail.includes('@')) e.personalEmail = 'Enter a valid email address';
      if (!form.currentAddress.trim())  e.currentAddress  = 'Current address is required';
      if (!form.emergencyName.trim())   e.emergencyName   = 'Emergency contact name is required';
      if (!form.emergencyRelation.trim()) e.emergencyRelation = 'Relation is required';
      if (!/^\d{10}$/.test(form.emergencyMobile)) e.emergencyMobile = 'Enter a valid 10-digit number';
    }
    if (s === 2) {
      if (!/^\d{12}$/.test(form.aadhaar)) e.aadhaar = 'Aadhaar must be exactly 12 digits';
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.pan.toUpperCase())) e.pan = 'Enter a valid PAN (e.g. ABCDE1234F)';
      if (!form.photoLink.trim())       e.photoLink = 'Profile photo (Google Drive link) is required';
    }
    if (s === 3) {
      if (!form.tenthSchool.trim())     e.tenthSchool   = 'School name is required';
      if (!form.tenthBoard)             e.tenthBoard    = 'Board is required';
      if (!form.tenthYear)              e.tenthYear     = 'Year of passing is required';
      if (!form.tenthPercent.trim())    e.tenthPercent  = 'Marks/percentage is required';
      if (!form.tenthDocLink.trim())    e.tenthDocLink  = 'Marksheet Drive link is required';
      if (!form.twelfthSchool.trim())   e.twelfthSchool = 'School name is required';
      if (!form.twelfthBoard)           e.twelfthBoard  = 'Board is required';
      if (!form.twelfthYear)            e.twelfthYear   = 'Year of passing is required';
      if (!form.twelfthPercent.trim())  e.twelfthPercent= 'Marks/percentage is required';
      if (!form.twelfthStream)          e.twelfthStream = 'Stream is required';
      if (!form.twelfthDocLink.trim())  e.twelfthDocLink= 'Marksheet Drive link is required';
      if (!form.gradCollege.trim())     e.gradCollege   = 'College name is required';
      if (!form.gradUniversity.trim())  e.gradUniversity= 'University is required';
      if (!form.gradDegree.trim())      e.gradDegree    = 'Degree is required';
      if (!form.gradBranch.trim())      e.gradBranch    = 'Branch/specialization is required';
      if (!form.gradYear)               e.gradYear      = 'Year of passing is required';
      if (!form.gradCgpa.trim())        e.gradCgpa      = 'CGPA/percentage is required';
      if (!form.gradDocLink.trim())     e.gradDocLink   = 'Degree/marksheet Drive link is required';
    }
    if (s === 4) {
      if (!form.joiningRole.trim())   e.joiningRole   = 'Role is required';
      if (!form.joiningDept.trim())   e.joiningDept   = 'Department is required';
      if (!form.joiningDate)          e.joiningDate   = 'Date of joining is required';
      if (!form.employmentType)       e.employmentType= 'Employment type is required';
    }
    if (s === 5) {
      if (!form.declaration) e.declaration = 'You must confirm the declaration to submit';
    }
    return e;
  };

  const next = () => {
    const e = validate(step);
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setErrors({});
    setStep(s => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prev = () => {
    setErrors({});
    setStep(s => s - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    const e = validate(5);
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setSubmitting(true);
    setSubmitError('');

    const payload = {
      // Personal
      'Full Name':                   form.fullName,
      'Date of Birth':               form.dob,
      'Gender':                      form.gender,
      'Blood Group':                 form.bloodGroup,
      'Mobile Number':               form.mobile,
      'Alternate Mobile':            form.alternateMobile,
      'Personal Email':              form.personalEmail,
      'Official Email':              form.officialEmail,
      'Current Address':             form.currentAddress,
      'Permanent Address':           form.sameAddress ? form.currentAddress : form.permanentAddress,
      'Emergency Contact Name':      form.emergencyName,
      'Emergency Relation':          form.emergencyRelation,
      'Emergency Mobile':            form.emergencyMobile,
      // Identity
      'Aadhaar Number':              form.aadhaar,
      'PAN Number':                  form.pan.toUpperCase(),
      'Passport Number':             form.passport || 'N/A',
      'Profile Photo (Drive Link)':  form.photoLink,
      // Education – 10th
      '10th School':                 form.tenthSchool,
      '10th Board':                  form.tenthBoard,
      '10th Year':                   form.tenthYear,
      '10th Percentage/Grade':       form.tenthPercent,
      '10th Marksheet (Drive Link)': form.tenthDocLink,
      // Education – 12th
      '12th School':                 form.twelfthSchool,
      '12th Board':                  form.twelfthBoard,
      '12th Year':                   form.twelfthYear,
      '12th Stream':                 form.twelfthStream,
      '12th Percentage/Grade':       form.twelfthPercent,
      '12th Marksheet (Drive Link)': form.twelfthDocLink,
      // Graduation
      'Grad College':                form.gradCollege,
      'Grad University':             form.gradUniversity,
      'Grad Degree':                 form.gradDegree,
      'Grad Branch':                 form.gradBranch,
      'Grad Year':                   form.gradYear,
      'Grad CGPA/Percentage':        form.gradCgpa,
      'Grad Documents (Drive Link)': form.gradDocLink,
      // Post Grad
      'PG College':                  form.pgCollege  || 'N/A',
      'PG University':               form.pgUniversity || 'N/A',
      'PG Degree':                   form.pgDegree   || 'N/A',
      'PG Branch':                   form.pgBranch   || 'N/A',
      'PG Year':                     form.pgYear     || 'N/A',
      'PG CGPA/Percentage':          form.pgCgpa     || 'N/A',
      'PG Documents (Drive Link)':   form.pgDocLink  || 'N/A',
      'Certifications':              form.certifications || 'N/A',
      // Professional
      'Joining Role':                form.joiningRole,
      'Department':                  form.joiningDept,
      'Date of Joining':             form.joiningDate,
      'Employment Type':             form.employmentType,
      'LinkedIn':                    form.linkedin   || 'N/A',
      'GitHub/Portfolio':            form.github     || 'N/A',
      'Previous Company':            form.prevCompany || 'N/A',
      'Previous Designation':        form.prevDesignation || 'N/A',
      'Previous Duration':           form.prevFrom && form.prevTo ? `${form.prevFrom} – ${form.prevTo}` : 'N/A',
      'Reason for Leaving':          form.prevReason || 'N/A',
      // Additional
      'T-Shirt Size':                form.tshirtSize || 'N/A',
      'Dietary Preference':          form.dietary    || 'N/A',
      'Medical Info':                form.medicalInfo || 'None disclosed',
    };

    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setSubmitError('Submission failed. Please try again or contact HR.');
      }
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Success screen ────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl shadow-xl max-w-md w-full p-10 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Onboarding Submitted!</h2>
          <p className="text-gray-500 leading-relaxed">
            Thank you, <strong>{form.fullName.split(' ')[0]}</strong>! Your onboarding details have been
            successfully submitted. Our HR team will review and get in touch with you shortly.
          </p>
          <p className="mt-4 text-sm text-gray-400">
            If you have any questions, reach out to <span className="text-blue-600">vibhraad@gmail.com</span>
          </p>
        </div>
      </div>
    );
  }

  const progress = ((step - 1) / (STEPS.length - 1)) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-lg font-bold text-gray-900">Employee Onboarding</h1>
              <p className="text-xs text-gray-400">Vibhraad Technology Pvt Ltd · Step {step} of {STEPS.length}</p>
            </div>
            <span className="text-sm font-semibold text-blue-600">{Math.round(progress)}% complete</span>
          </div>
          {/* Progress bar */}
          <div className="w-full bg-gray-100 rounded-full h-1.5">
            <div
              className="bg-blue-600 h-1.5 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Step tabs */}
      <div className="bg-white border-b overflow-x-auto">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex gap-0 min-w-max">
            {STEPS.map((s) => {
              const Icon = s.icon;
              const done = s.id < step;
              const active = s.id === step;
              return (
                <div
                  key={s.id}
                  className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium border-b-2 transition-colors whitespace-nowrap
                    ${active ? 'border-blue-600 text-blue-600' : done ? 'border-green-500 text-green-600' : 'border-transparent text-gray-400'}`}
                >
                  {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                  {s.label}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Form body */}
      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* ── STEP 1: Personal Information ──────────────────────────────────── */}
        {step === 1 && (
          <Section title="Personal Information" icon={<User className="w-5 h-5" />}>
            <Grid2>
              <InputField label="Full Name" required error={errors.fullName}>
                <input className={textInput(errors.fullName)} value={form.fullName}
                  onChange={e => set('fullName', e.target.value)} placeholder="As per government ID" />
              </InputField>
              <InputField label="Date of Birth" required error={errors.dob}>
                <input type="date" className={textInput(errors.dob)} value={form.dob}
                  onChange={e => set('dob', e.target.value)} />
              </InputField>
              <InputField label="Gender" required error={errors.gender}>
                <select className={selectInput(errors.gender)} value={form.gender}
                  onChange={e => set('gender', e.target.value)}>
                  <option value="">Select gender</option>
                  <option>Male</option><option>Female</option><option>Non-binary</option><option>Prefer not to say</option>
                </select>
              </InputField>
              <InputField label="Blood Group" required error={errors.bloodGroup}>
                <select className={selectInput(errors.bloodGroup)} value={form.bloodGroup}
                  onChange={e => set('bloodGroup', e.target.value)}>
                  <option value="">Select blood group</option>
                  {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(g => <option key={g}>{g}</option>)}
                </select>
              </InputField>
              <InputField label="Mobile Number" required error={errors.mobile} hint="10-digit Indian mobile number">
                <input className={textInput(errors.mobile)} value={form.mobile} maxLength={10}
                  onChange={e => set('mobile', e.target.value.replace(/\D/g, ''))} placeholder="9XXXXXXXXX" />
              </InputField>
              <InputField label="Alternate Mobile Number" error={errors.alternateMobile}>
                <input className={textInput()} value={form.alternateMobile} maxLength={10}
                  onChange={e => set('alternateMobile', e.target.value.replace(/\D/g, ''))} placeholder="Optional" />
              </InputField>
              <InputField label="Personal Email Address" required error={errors.personalEmail}>
                <input type="email" className={textInput(errors.personalEmail)} value={form.personalEmail}
                  onChange={e => set('personalEmail', e.target.value)} placeholder="you@gmail.com" />
              </InputField>
              <InputField label="Official / Work Email" error={errors.officialEmail} hint="Leave blank if not yet assigned">
                <input type="email" className={textInput()} value={form.officialEmail}
                  onChange={e => set('officialEmail', e.target.value)} placeholder="you@vibhraad.com" />
              </InputField>
            </Grid2>

            <div className="mt-5 space-y-4">
              <InputField label="Current Address" required error={errors.currentAddress}>
                <textarea rows={3} className={textInput(errors.currentAddress)} value={form.currentAddress}
                  onChange={e => set('currentAddress', e.target.value)} placeholder="House no., Street, City, State, PIN" />
              </InputField>

              <div className="flex items-center gap-2">
                <input type="checkbox" id="sameAddr" checked={form.sameAddress}
                  onChange={e => set('sameAddress', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600" />
                <label htmlFor="sameAddr" className="text-sm text-gray-600">Permanent address same as current address</label>
              </div>

              {!form.sameAddress && (
                <InputField label="Permanent Address" error={errors.permanentAddress}>
                  <textarea rows={3} className={textInput()} value={form.permanentAddress}
                    onChange={e => set('permanentAddress', e.target.value)} placeholder="House no., Street, City, State, PIN" />
                </InputField>
              )}
            </div>

            <SectionDivider title="Emergency Contact" icon={<Heart className="w-4 h-4" />} />
            <Grid3>
              <InputField label="Contact Name" required error={errors.emergencyName}>
                <input className={textInput(errors.emergencyName)} value={form.emergencyName}
                  onChange={e => set('emergencyName', e.target.value)} placeholder="Full name" />
              </InputField>
              <InputField label="Relationship" required error={errors.emergencyRelation}>
                <select className={selectInput(errors.emergencyRelation)} value={form.emergencyRelation}
                  onChange={e => set('emergencyRelation', e.target.value)}>
                  <option value="">Select</option>
                  {['Father','Mother','Spouse','Sibling','Friend','Other'].map(r => <option key={r}>{r}</option>)}
                </select>
              </InputField>
              <InputField label="Mobile Number" required error={errors.emergencyMobile}>
                <input className={textInput(errors.emergencyMobile)} value={form.emergencyMobile} maxLength={10}
                  onChange={e => set('emergencyMobile', e.target.value.replace(/\D/g, ''))} placeholder="10-digit mobile" />
              </InputField>
            </Grid3>
          </Section>
        )}

        {/* ── STEP 2: Identity Documents ─────────────────────────────────────── */}
        {step === 2 && (
          <Section title="Identity Documents" icon={<Shield className="w-5 h-5" />}>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 text-sm text-blue-700 flex gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>All document links must be Google Drive links with <strong>view access set to "Anyone with the link"</strong>.</span>
            </div>

            <Grid2>
              <InputField label="Aadhaar Number" required error={errors.aadhaar} hint="12-digit Aadhaar UID">
                <input className={textInput(errors.aadhaar)} value={form.aadhaar} maxLength={12}
                  onChange={e => set('aadhaar', e.target.value.replace(/\D/g, ''))} placeholder="XXXX XXXX XXXX" />
              </InputField>
              <InputField label="PAN Card Number" required error={errors.pan} hint="Format: ABCDE1234F">
                <input className={textInput(errors.pan)} value={form.pan} maxLength={10}
                  onChange={e => set('pan', e.target.value.toUpperCase())} placeholder="ABCDE1234F" />
              </InputField>
              <InputField label="Passport Number" error={errors.passport} hint="Optional — leave blank if not applicable">
                <input className={textInput()} value={form.passport}
                  onChange={e => set('passport', e.target.value.toUpperCase())} placeholder="A1234567" />
              </InputField>
              <InputField label="Profile Photo — Google Drive Link" required error={errors.photoLink}
                hint="Recent passport-size photo, clear background">
                <input className={textInput(errors.photoLink)} value={form.photoLink}
                  onChange={e => set('photoLink', e.target.value)} placeholder="https://drive.google.com/file/d/..." />
              </InputField>
            </Grid2>
          </Section>
        )}

        {/* ── STEP 3: Education Details ──────────────────────────────────────── */}
        {step === 3 && (
          <Section title="Education Details" icon={<GraduationCap className="w-5 h-5" />}>

            <SectionDivider title="10th Standard" />
            <Grid2>
              <InputField label="School Name" required error={errors.tenthSchool}>
                <input className={textInput(errors.tenthSchool)} value={form.tenthSchool}
                  onChange={e => set('tenthSchool', e.target.value)} placeholder="School name" />
              </InputField>
              <InputField label="Board" required error={errors.tenthBoard}>
                <select className={selectInput(errors.tenthBoard)} value={form.tenthBoard}
                  onChange={e => set('tenthBoard', e.target.value)}>
                  <option value="">Select board</option>
                  {['CBSE','ICSE','UP Board','MP Board','Bihar Board','Rajasthan Board','Maharashtra Board','Other State Board'].map(b=><option key={b}>{b}</option>)}
                </select>
              </InputField>
              <InputField label="Year of Passing" required error={errors.tenthYear}>
                <input type="number" min="2000" max="2030" className={textInput(errors.tenthYear)} value={form.tenthYear}
                  onChange={e => set('tenthYear', e.target.value)} placeholder="e.g. 2018" />
              </InputField>
              <InputField label="Percentage / Grade" required error={errors.tenthPercent}>
                <input className={textInput(errors.tenthPercent)} value={form.tenthPercent}
                  onChange={e => set('tenthPercent', e.target.value)} placeholder="e.g. 85% or A1" />
              </InputField>
              <div className="md:col-span-2">
                <InputField label="Marksheet — Google Drive Link" required error={errors.tenthDocLink}
                  hint="Share the marksheet via Google Drive with 'Anyone with link' access">
                  <input className={textInput(errors.tenthDocLink)} value={form.tenthDocLink}
                    onChange={e => set('tenthDocLink', e.target.value)} placeholder="https://drive.google.com/file/d/..." />
                </InputField>
              </div>
            </Grid2>

            <SectionDivider title="12th Standard" />
            <Grid2>
              <InputField label="School Name" required error={errors.twelfthSchool}>
                <input className={textInput(errors.twelfthSchool)} value={form.twelfthSchool}
                  onChange={e => set('twelfthSchool', e.target.value)} placeholder="School name" />
              </InputField>
              <InputField label="Board" required error={errors.twelfthBoard}>
                <select className={selectInput(errors.twelfthBoard)} value={form.twelfthBoard}
                  onChange={e => set('twelfthBoard', e.target.value)}>
                  <option value="">Select board</option>
                  {['CBSE','ICSE','UP Board','MP Board','Bihar Board','Rajasthan Board','Maharashtra Board','Other State Board'].map(b=><option key={b}>{b}</option>)}
                </select>
              </InputField>
              <InputField label="Stream" required error={errors.twelfthStream}>
                <select className={selectInput(errors.twelfthStream)} value={form.twelfthStream}
                  onChange={e => set('twelfthStream', e.target.value)}>
                  <option value="">Select stream</option>
                  {['Science (PCM)','Science (PCB)','Commerce','Arts / Humanities','Vocational'].map(s=><option key={s}>{s}</option>)}
                </select>
              </InputField>
              <InputField label="Year of Passing" required error={errors.twelfthYear}>
                <input type="number" min="2000" max="2030" className={textInput(errors.twelfthYear)} value={form.twelfthYear}
                  onChange={e => set('twelfthYear', e.target.value)} placeholder="e.g. 2020" />
              </InputField>
              <InputField label="Percentage / Grade" required error={errors.twelfthPercent}>
                <input className={textInput(errors.twelfthPercent)} value={form.twelfthPercent}
                  onChange={e => set('twelfthPercent', e.target.value)} placeholder="e.g. 78%" />
              </InputField>
              <InputField label="Marksheet — Google Drive Link" required error={errors.twelfthDocLink}>
                <input className={textInput(errors.twelfthDocLink)} value={form.twelfthDocLink}
                  onChange={e => set('twelfthDocLink', e.target.value)} placeholder="https://drive.google.com/file/d/..." />
              </InputField>
            </Grid2>

            <SectionDivider title="Graduation (BTech / BE / BSc / BCA / Other)" />
            <Grid2>
              <InputField label="College Name" required error={errors.gradCollege}>
                <input className={textInput(errors.gradCollege)} value={form.gradCollege}
                  onChange={e => set('gradCollege', e.target.value)} placeholder="College / Institute name" />
              </InputField>
              <InputField label="University" required error={errors.gradUniversity}>
                <input className={textInput(errors.gradUniversity)} value={form.gradUniversity}
                  onChange={e => set('gradUniversity', e.target.value)} placeholder="Affiliated university" />
              </InputField>
              <InputField label="Degree" required error={errors.gradDegree}>
                <input className={textInput(errors.gradDegree)} value={form.gradDegree}
                  onChange={e => set('gradDegree', e.target.value)} placeholder="e.g. B.Tech, BCA, BSc" />
              </InputField>
              <InputField label="Branch / Specialization" required error={errors.gradBranch}>
                <input className={textInput(errors.gradBranch)} value={form.gradBranch}
                  onChange={e => set('gradBranch', e.target.value)} placeholder="e.g. Computer Science" />
              </InputField>
              <InputField label="Year of Passing" required error={errors.gradYear}>
                <input type="number" min="2000" max="2030" className={textInput(errors.gradYear)} value={form.gradYear}
                  onChange={e => set('gradYear', e.target.value)} placeholder="e.g. 2023" />
              </InputField>
              <InputField label="CGPA / Percentage" required error={errors.gradCgpa}>
                <input className={textInput(errors.gradCgpa)} value={form.gradCgpa}
                  onChange={e => set('gradCgpa', e.target.value)} placeholder="e.g. 8.5 CGPA or 79%" />
              </InputField>
              <div className="md:col-span-2">
                <InputField label="Degree Certificate + Marksheets — Google Drive Link" required error={errors.gradDocLink}
                  hint="Upload a folder or combined PDF with all semester marksheets and degree certificate">
                  <input className={textInput(errors.gradDocLink)} value={form.gradDocLink}
                    onChange={e => set('gradDocLink', e.target.value)} placeholder="https://drive.google.com/drive/folders/..." />
                </InputField>
              </div>
            </Grid2>

            <SectionDivider title="Post Graduation (MTech / MBA / MSc — Optional)" />
            <Grid2>
              <InputField label="College Name" error={errors.pgCollege}>
                <input className={textInput()} value={form.pgCollege}
                  onChange={e => set('pgCollege', e.target.value)} placeholder="Optional" />
              </InputField>
              <InputField label="University" error={errors.pgUniversity}>
                <input className={textInput()} value={form.pgUniversity}
                  onChange={e => set('pgUniversity', e.target.value)} placeholder="Optional" />
              </InputField>
              <InputField label="Degree" error={errors.pgDegree}>
                <input className={textInput()} value={form.pgDegree}
                  onChange={e => set('pgDegree', e.target.value)} placeholder="e.g. M.Tech, MBA" />
              </InputField>
              <InputField label="Branch / Specialization" error={errors.pgBranch}>
                <input className={textInput()} value={form.pgBranch}
                  onChange={e => set('pgBranch', e.target.value)} placeholder="Optional" />
              </InputField>
              <InputField label="Year of Passing" error={errors.pgYear}>
                <input type="number" min="2000" max="2030" className={textInput()} value={form.pgYear}
                  onChange={e => set('pgYear', e.target.value)} placeholder="Optional" />
              </InputField>
              <InputField label="CGPA / Percentage" error={errors.pgCgpa}>
                <input className={textInput()} value={form.pgCgpa}
                  onChange={e => set('pgCgpa', e.target.value)} placeholder="Optional" />
              </InputField>
              <div className="md:col-span-2">
                <InputField label="PG Documents — Google Drive Link" error={errors.pgDocLink} hint="Optional">
                  <input className={textInput()} value={form.pgDocLink}
                    onChange={e => set('pgDocLink', e.target.value)} placeholder="https://drive.google.com/..." />
                </InputField>
              </div>
            </Grid2>

            <SectionDivider title="Certifications / Courses (Optional)" />
            <InputField label="List any relevant certifications or online courses" error={errors.certifications}
              hint="e.g. AWS Solutions Architect, Google Data Analytics, Meta Front-End Developer">
              <textarea rows={3} className={textInput()} value={form.certifications}
                onChange={e => set('certifications', e.target.value)}
                placeholder="Course name — Issuing body — Year&#10;Course name — Issuing body — Year" />
            </InputField>
          </Section>
        )}

        {/* ── STEP 4: Professional Details ──────────────────────────────────── */}
        {step === 4 && (
          <Section title="Professional Details" icon={<Briefcase className="w-5 h-5" />}>
            <SectionDivider title="Role at Vibhraad" icon={<Building2 className="w-4 h-4" />} />
            <Grid2>
              <InputField label="Position / Role" required error={errors.joiningRole}>
                <input className={textInput(errors.joiningRole)} value={form.joiningRole}
                  onChange={e => set('joiningRole', e.target.value)} placeholder="e.g. Frontend Developer Intern" />
              </InputField>
              <InputField label="Department" required error={errors.joiningDept}>
                <select className={selectInput(errors.joiningDept)} value={form.joiningDept}
                  onChange={e => set('joiningDept', e.target.value)}>
                  <option value="">Select department</option>
                  {['Engineering','AI / ML','Design','Digital Marketing','Sales & BD','HR','Operations','Finance','Management'].map(d=><option key={d}>{d}</option>)}
                </select>
              </InputField>
              <InputField label="Date of Joining" required error={errors.joiningDate}>
                <input type="date" className={textInput(errors.joiningDate)} value={form.joiningDate}
                  onChange={e => set('joiningDate', e.target.value)} />
              </InputField>
              <InputField label="Employment Type" required error={errors.employmentType}>
                <select className={selectInput(errors.employmentType)} value={form.employmentType}
                  onChange={e => set('employmentType', e.target.value)}>
                  <option value="">Select type</option>
                  {['Full-time','Part-time','Internship','Contract / Freelance'].map(t=><option key={t}>{t}</option>)}
                </select>
              </InputField>
              <InputField label="LinkedIn Profile URL" error={errors.linkedin} hint="Optional but recommended">
                <input className={textInput()} value={form.linkedin}
                  onChange={e => set('linkedin', e.target.value)} placeholder="https://linkedin.com/in/..." />
              </InputField>
              <InputField label="GitHub / Portfolio URL" error={errors.github} hint="Optional">
                <input className={textInput()} value={form.github}
                  onChange={e => set('github', e.target.value)} placeholder="https://github.com/..." />
              </InputField>
            </Grid2>

            <SectionDivider title="Previous Employment (if any — leave blank for freshers)" />
            <Grid2>
              <InputField label="Previous Company Name" error={errors.prevCompany}>
                <input className={textInput()} value={form.prevCompany}
                  onChange={e => set('prevCompany', e.target.value)} placeholder="Company name" />
              </InputField>
              <InputField label="Previous Designation" error={errors.prevDesignation}>
                <input className={textInput()} value={form.prevDesignation}
                  onChange={e => set('prevDesignation', e.target.value)} placeholder="e.g. Junior Developer" />
              </InputField>
              <InputField label="From (Month / Year)" error={errors.prevFrom}>
                <input type="month" className={textInput()} value={form.prevFrom}
                  onChange={e => set('prevFrom', e.target.value)} />
              </InputField>
              <InputField label="To (Month / Year)" error={errors.prevTo}>
                <input type="month" className={textInput()} value={form.prevTo}
                  onChange={e => set('prevTo', e.target.value)} />
              </InputField>
              <div className="md:col-span-2">
                <InputField label="Reason for Leaving" error={errors.prevReason}>
                  <input className={textInput()} value={form.prevReason}
                    onChange={e => set('prevReason', e.target.value)} placeholder="Optional" />
                </InputField>
              </div>
            </Grid2>
          </Section>
        )}

        {/* ── STEP 5: Review & Submit ────────────────────────────────────────── */}
        {step === 5 && (
          <Section title="Review & Submit" icon={<CheckCircle2 className="w-5 h-5" />}>
            <p className="text-sm text-gray-500 mb-6">
              Please review your details below. Use the <strong>Back</strong> button to make corrections before submitting.
            </p>

            <div className="space-y-5">
              <ReviewBlock title="Personal Information">
                <ReviewRow label="Full Name"          value={form.fullName} />
                <ReviewRow label="Date of Birth"      value={form.dob} />
                <ReviewRow label="Gender"             value={form.gender} />
                <ReviewRow label="Blood Group"        value={form.bloodGroup} />
                <ReviewRow label="Mobile"             value={form.mobile} />
                <ReviewRow label="Personal Email"     value={form.personalEmail} />
                <ReviewRow label="Current Address"    value={form.currentAddress} />
                <ReviewRow label="Emergency Contact"  value={`${form.emergencyName} (${form.emergencyRelation}) — ${form.emergencyMobile}`} />
              </ReviewBlock>

              <ReviewBlock title="Identity Documents">
                <ReviewRow label="Aadhaar"     value={`XXXX XXXX ${form.aadhaar.slice(-4)}`} />
                <ReviewRow label="PAN"         value={form.pan.toUpperCase()} />
                <ReviewRow label="Photo Link"  value={form.photoLink} link />
              </ReviewBlock>

              <ReviewBlock title="Education">
                <ReviewRow label="10th"  value={`${form.tenthSchool} · ${form.tenthBoard} · ${form.tenthYear} · ${form.tenthPercent}`} />
                <ReviewRow label="12th"  value={`${form.twelfthSchool} · ${form.twelfthBoard} · ${form.twelfthYear} · ${form.twelfthStream} · ${form.twelfthPercent}`} />
                <ReviewRow label="Graduation" value={`${form.gradDegree} in ${form.gradBranch} — ${form.gradCollege}, ${form.gradYear} · ${form.gradCgpa}`} />
                {form.pgDegree && <ReviewRow label="Post Grad" value={`${form.pgDegree} — ${form.pgCollege}, ${form.pgYear}`} />}
              </ReviewBlock>

              <ReviewBlock title="Professional Details">
                <ReviewRow label="Role"            value={form.joiningRole} />
                <ReviewRow label="Department"      value={form.joiningDept} />
                <ReviewRow label="Date of Joining" value={form.joiningDate} />
                <ReviewRow label="Type"            value={form.employmentType} />
              </ReviewBlock>

            </div>

            {/* Declaration */}
            <div className={`mt-6 p-5 rounded-2xl border-2 ${errors.declaration ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
              <h4 className="font-semibold text-gray-800 mb-2">Declaration</h4>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                I hereby declare that all the information provided in this onboarding form is true, correct, and
                complete to the best of my knowledge. I understand that any false or misleading information may
                result in the termination of my employment. I consent to Vibhraad Technology Pvt Ltd storing and
                processing this information for HR and administrative purposes.
              </p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600"
                  checked={form.declaration} onChange={e => set('declaration', e.target.checked)} />
                <span className="text-sm font-medium text-gray-700">
                  I confirm that all information provided is true and accurate, and I agree to the above declaration.
                  <span className="text-red-500 ml-0.5">*</span>
                </span>
              </label>
              {errors.declaration && (
                <p className="mt-2 text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />{errors.declaration}
                </p>
              )}
            </div>

            {submitError && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />{submitError}
              </div>
            )}
          </Section>
        )}

        {/* Navigation buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t">
          {step > 1 ? (
            <button onClick={prev}
              className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
          ) : <div />}

          {step < STEPS.length ? (
            <button onClick={next}
              className="flex items-center gap-2 px-7 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-500 transition-colors">
              Save & Continue <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={submitting}
              className="flex items-center gap-2 px-8 py-3 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <><CheckCircle2 className="w-4 h-4" /> Submit Onboarding</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Small helper components ──────────────────────────────────────────────────
function Section({ title, icon, children }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
      <div className="flex items-center gap-2 mb-6 pb-4 border-b">
        <span className="text-blue-600">{icon}</span>
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function SectionDivider({ title, icon }) {
  return (
    <div className="flex items-center gap-2 mt-8 mb-4">
      {icon && <span className="text-gray-500">{icon}</span>}
      <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">{title}</h3>
      <div className="flex-1 h-px bg-gray-200" />
    </div>
  );
}

function Grid2({ children }) {
  return <div className="grid md:grid-cols-2 gap-5">{children}</div>;
}

function Grid3({ children }) {
  return <div className="grid md:grid-cols-3 gap-5">{children}</div>;
}

function ReviewBlock({ title, children }) {
  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden">
      <div className="bg-gray-50 px-5 py-3 border-b">
        <h4 className="text-sm font-bold text-gray-700">{title}</h4>
      </div>
      <div className="divide-y">{children}</div>
    </div>
  );
}

function ReviewRow({ label, value, link }) {
  return (
    <div className="flex gap-4 px-5 py-3 text-sm">
      <span className="w-36 flex-shrink-0 text-gray-500 font-medium">{label}</span>
      {link
        ? <a href={value} target="_blank" rel="noopener noreferrer"
            className="text-blue-600 underline break-all">{value || '—'}</a>
        : <span className="text-gray-800 break-words">{value || '—'}</span>
      }
    </div>
  );
}
